import { PDFDocument, StandardFonts } from "pdf-lib";
import { winAnsiSafe } from "./exportHelpers";

/*
	Shared pdf-lib helpers for the exports: merging uploaded PDF
	documents into a generated file and writing simple text pages
	(indexes, head sheets, cover sheets) with the standard fonts.

	The page geometry matches the jsPDF part of the diary export: A4
	in points with the same margins. pdf-lib's origin is the
	bottom-left corner; the writer below hides that and counts from
	the top like jsPDF does.
*/

export const PAGE_WIDTH  = 595.28;
export const PAGE_HEIGHT = 841.89;
export const LEFT = 70;
export const TOP  = 70;
export const LINE = 15;

/**
 * Load generated PDF bytes -- or start an empty document when no
 * bytes are given -- and embed the standard fonts.
 */
export async function createMergeContext(pdfBytes) {
	const merged = pdfBytes
		? await PDFDocument.load(pdfBytes)
		: await PDFDocument.create();
	return {
		merged,
		regular : await merged.embedFont(StandardFonts.Helvetica),
		bold    : await merged.embedFont(StandardFonts.HelveticaBold),
	};
}

/**
 * Fetch and parse a PDF. Returns undefined when it cannot be loaded
 * (missing, encrypted, not actually a PDF) -- callers mark those
 * instead of failing the whole export.
 */
export async function fetchPdf(url) {
	try {
		const response = await fetch(url);
		if (!response.ok) { throw new Error("HTTP " + response.status); }
		return await PDFDocument.load(await response.arrayBuffer());
	}
	catch (error) {
		return undefined;
	}
}

/** Append all pages of a source document to the merge document. */
export async function appendPages(context, source) {
	const pages = await context.merged.copyPages(source,
		source.getPageIndices());
	for (const page of pages) { context.merged.addPage(page); }
}

/**
 * Embed a data-URI logo (PNG or JPEG). Returns undefined when the
 * image cannot be embedded.
 */
export async function embedLogo(context, dataUri) {
	if (!dataUri) { return undefined; }
	try {
		const uri = String(dataUri).trim();
		if (uri.startsWith("data:image/png")) {
			return await context.merged.embedPng(uri);
		}
		return await context.merged.embedJpg(uri);
	}
	catch (error) {
		return undefined;
	}
}

/** Truncate text with an ellipsis so it fits maxWidth at size. */
export function truncateToWidth(font, text, size, maxWidth) {
	if (font.widthOfTextAtSize(text, size) <= maxWidth) { return text; }
	const ellipsis = "…";
	for (let end = text.length; end > 0; end--) {
		const candidate = text.substring(0, end).trimEnd() + ellipsis;
		if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
			return candidate;
		}
	}
	return ellipsis;
}

/**
 * Cursor-style writer for text pages. Starts a page on first use,
 * breaks on overflow, and passes all text through ``winAnsiSafe``
 * (the standard fonts cannot encode anything outside WinAnsi).
 */
export function createTextPageWriter(context) {
	let page = undefined;
	let y = 0;

	const newPage = () => {
		page = context.merged.addPage([ PAGE_WIDTH, PAGE_HEIGHT ]);
		y = PAGE_HEIGHT - TOP;
	};
	const ensureRoom = () => {
		if (!page || y < TOP) { newPage(); }
	};

	return {
		newPage,

		heading(text, size = 13) {
			ensureRoom();
			page.drawText(winAnsiSafe(text),
				{ x: LEFT, y, size, font: context.bold });
			y -= 2 * LINE;
		},

		/** options: indent (pt), size (default 10), bold. */
		line(text, options = {}) {
			ensureRoom();
			page.drawText(winAnsiSafe(text), {
				x    : LEFT + (options.indent || 0),
				y,
				size : options.size || 10,
				font : options.bold ? context.bold : context.regular,
			});
			y -= LINE;
		},

		space() { y -= LINE; },

		/**
		 * A row of aligned columns. cells: [ { text, x, width, bold } ]
		 * with x relative to the left margin; every cell is truncated
		 * with an ellipsis to its column width.
		 */
		row(cells, options = {}) {
			ensureRoom();
			const size = options.size || 10;
			for (const cell of cells) {
				const font = (cell.bold || options.bold)
					? context.bold : context.regular;
				const text = truncateToWidth(font,
					winAnsiSafe(cell.text || ""), size, cell.width);
				page.drawText(text, { x: LEFT + cell.x, y, size, font });
			}
			y -= LINE;
		},

		/** Place an embedded image at an absolute top-based position. */
		drawImage(embedded, x, top, width, height) {
			ensureRoom();
			page.drawImage(embedded,
				{ x, y: PAGE_HEIGHT - top - height, width, height });
		},

		/**
		 * Move the cursor down to at least ``top`` (in top-down
		 * points), so following text clears content drawn at a fixed
		 * position, e.g. the letterhead logo.
		 */
		ensureBelow(top) {
			ensureRoom();
			y = Math.min(y, PAGE_HEIGHT - top);
		},
	};
}
